import React, { type JSX } from 'react';

import { List, Stack, ListItem, Typography, ListItemIcon, ListItemText, ListItemButton, Tooltip } from '@mui/material';

import { useTheme } from '@mui/material/styles';
import CustomIcon from '../../../components/CustomIcon';
import { TypesSkeleton } from './Skeletons';
import { useHandleAddWidgets } from 'pages/DashboardAndWidgets/AddWidget/hooks';
import { useGetAllWidgetTypesQuery } from '@fiji/common/src/features/widgetManagement/widgetApi';
import { addWidgetModalData, setPayload } from '@fiji/common/src/features/widgetManagement/widgetSlice';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { CustomTransComponent } from 'components';

export const WidgetTypesGrid = (): JSX.Element => {
    const theme: any = useTheme();
    const dispatch = useAppDispatch();

    const modalControls = useTypedSelector(addWidgetModalData);

    const { handleIsSelected } = useHandleAddWidgets();

    const {
        currentData: widgetTypes,
        isLoading: widgetTypesLoading,
        isFetching: widgetTypesFetching,
        isSuccess,
        isError,
    }: any = useGetAllWidgetTypesQuery(
        {},
        {
            skip: !modalControls?.isOpen,
        }
    );

    return (
        <Stack py={3} px={2} className="bg-content-wrapper">
            <Typography color="primary" fontSize={'16px'} variant="subtitle1" fontWeight={'600'}>
                <CustomTransComponent translationKey={'WIDGETS:ADD_WIDGET_MODAL.FILTER_TYPE'} />
            </Typography>
            <List
                data-testid="widget-types-list"
                disablePadding
                className="margin-top-24 bg-white overflow-y-scroll"
                sx={{ height: 'calc(100vh - 316px) !important' }}
            >
                {(widgetTypesLoading || widgetTypesFetching) && !(isSuccess || isError) && <TypesSkeleton items={9} />}
                {Boolean(widgetTypes?.data?.records?.length) &&
                    !(widgetTypesLoading || widgetTypesFetching) &&
                    widgetTypes?.data?.records?.map((item: any, index: number) => (
                        <Tooltip key={item.id} followCursor title={item?.description ?? ''}>
                            <ListItem
                                data-testid={`widget-type-${index}`}
                                className="border-bottom-1"
                                sx={{
                                    '&:hover': {
                                        backgroundColor: theme?.palette?.primary?.[50],
                                    },
                                    backgroundColor: handleIsSelected('widgetType', item)
                                        ? theme?.palette?.primary?.[50]
                                        : '',
                                    color: handleIsSelected('widgetType', item) ? theme?.palette?.primary?.main : '',
                                }}
                                onClick={(): void => {
                                    dispatch(setPayload({ key: 'widgetType', value: item }));
                                }}
                            >
                                <ListItemButton className="list-item-button" role={undefined} dense>
                                    <ListItemIcon>
                                        <CustomIcon family={item?.icon?.web?.family} iconName={item?.icon?.web?.name} />
                                    </ListItemIcon>
                                    <ListItemText primary={item?.label} />
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>
                    ))}
            </List>
        </Stack>
    );
};

export default WidgetTypesGrid;
